// src/pages/familyoffice/FamilyOfficeComingSoon.jsx
import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Landmark, Building2, Scale, ShieldCheck } from "lucide-react";

const MODULES = {
  "museum-loans": {
    icon: Landmark,
    label: "Prêts institutionnels",
    description:
      "Identifiez les œuvres de vos familles clientes prêtables à des musées et institutions culturelles. Suivi des dossiers, contrats de prêt, couverture assurance et valorisation de la visibilité.",
    features: [
      "Matching œuvres ↔ expositions en cours",
      "Génération de dossiers de prêt",
      "Suivi des contrats et restitutions",
      "Impact sur valorisation et visibilité",
    ],
  },
  "auction-houses": {
    icon: Building2,
    label: "Maisons de vente",
    description:
      "Suivez les ventes aux enchères pertinentes pour vos familles clientes — comparables, opportunités d'acquisition, estimations de marché en temps réel.",
    features: [
      "Veille ventes par artiste et style",
      "Comparables automatiques pour estimations",
      "Alertes adjudications notables",
      "Export pour rapports patrimoniaux",
    ],
  },
  "fiscal-optimization": {
    icon: Scale,
    label: "Planification fiscale",
    description:
      "Simulez les implications fiscales des scénarios de transmission, donation et vente pour chaque famille cliente, selon leur pays de résidence et leur régime matrimonial.",
    features: [
      "Simulation donation / démembrement / legs",
      "Multi-juridictions (France, Belgique, Suisse)",
      "Calcul droits de succession par scénario",
      "Rapport synthétique pour notaire / avocat",
    ],
  },
  insurance: {
    icon: ShieldCheck,
    label: "Module Assurance",
    description:
      "Auditez la couverture assurance des collections de vos familles clientes. Détectez les sous-assurances, suivez les renouvellements et simulez les ajustements de garanties.",
    features: [
      "Taux de couverture vs valorisation Artkiv",
      "Alertes renouvellements et exclusions",
      "Simulation ajustement garanties",
      "Export pour courtiers spécialisés",
    ],
  },
};

const DEFAULT_MODULE = {
  icon: Clock,
  label: "Module en développement",
  description: "Ce module est en cours de développement et sera disponible prochainement.",
  features: [],
};

export default function FamilyOfficeComingSoon() {
  const [searchParams] = useSearchParams();
  const moduleKey = searchParams.get("module") || "";
  const module = MODULES[moduleKey] || DEFAULT_MODULE;
  const Icon = module.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="border-b border-[#E8E8E8] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center gap-4">
          <Link
            to="/family-office/dashboard"
            className="flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
          >
            <ArrowLeft size={16} />
            Retour au dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-20">
        {/* Icône + titre */}
        <div className="flex flex-col items-center text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-[#F9F7F4] border border-[#E8E0D0] flex items-center justify-center mb-6">
            <Icon size={28} className="text-[#C9A961]" strokeWidth={1.5} />
          </div>
          <p className="text-xs font-medium text-[#C9A961] tracking-widest uppercase mb-3">
            En développement
          </p>
          <h1
            className="text-3xl font-light text-[#1A1A1A] mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {module.label}
          </h1>
          <p className="text-base text-[#666] leading-relaxed max-w-xl">
            {module.description}
          </p>
        </div>

        {/* Ligne dorée */}
        <div className="w-16 h-px bg-[#C9A961] mx-auto mb-12" />

        {/* Features à venir */}
        {module.features.length > 0 && (
          <div className="bg-white border border-[#E8E8E8] rounded-lg p-8 mb-10">
            <p
              className="text-xs font-medium text-[#999] tracking-widest uppercase mb-6"
            >
              Ce que ce module permettra
            </p>
            <ul className="space-y-4">
              {module.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C9A961] mt-2 flex-shrink-0" />
                  <span className="text-sm text-[#444] leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <p className="text-sm text-[#999] mb-6">
            Ce module sera disponible dans une prochaine version d'Artkiv.
          </p>
          <Link
            to="/family-office/dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
          >
            <ArrowLeft size={15} />
            Retour au dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}